<script setup>
  import { computed, inject, ref, onBeforeUnmount } from 'vue'
  import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage'
  import { getApp } from 'firebase/app'
  import { handleErrors } from '../../utils/errorHandler'
  import AlertNotification from '../AlertNotification.vue'

  const props = defineProps({
    downloads: { type: Array, default: () => [] },
    courseId: { type: String, required: true },
    lessonId: { type: String, required: true }
  })

  const toggleLoadingWithMessage = inject('toggleLoadingWithMessage', () => {})

  const hasDownloads = computed(() => props.downloads && props.downloads.length > 0)

  // Inline error alert with auto-dismiss, same UX as Content Library downloads.
  const ALERT_AUTO_DISMISS_MS = 6000
  const DOWNLOAD_ERROR_MESSAGE = 'We couldn\'t download the file. Please try again later.'

  const showDownloadError = ref(false)
  let dismissTimerId = null

  const clearDismissTimer = () => {
    if (dismissTimerId) {
      clearTimeout(dismissTimerId)
      dismissTimerId = null
    }
  }

  const dismissDownloadError = () => {
    clearDismissTimer()
    showDownloadError.value = false
  }

  const reportDownloadError = () => {
    clearDismissTimer()
    showDownloadError.value = true
    dismissTimerId = setTimeout(() => {
      showDownloadError.value = false
      dismissTimerId = null
    }, ALERT_AUTO_DISMISS_MS)
  }

  onBeforeUnmount(() => {
    clearDismissTimer()
  })

  function getFileExtension(fileName) {
    const cleanValue = String(fileName || '').toLowerCase().split('?')[0].split('#')[0]
    const segments = cleanValue.split('.')
    return segments.length > 1 ? segments.pop() : ''
  }

  function getFileIcon(fileName) {
    const ext = getFileExtension(fileName)

    if (ext === 'pdf') return 'bi-filetype-pdf'
    if (['doc', 'docx'].includes(ext)) return 'bi-file-earmark-word'
    if (['xls', 'xlsx'].includes(ext)) return 'bi-filetype-xls'
    if (['ppt', 'pptx'].includes(ext)) return 'bi-file-earmark-ppt-fill'
    if (ext === 'zip') return 'bi-file-earmark-zip'

    return 'bi-download'
  }

  function getFileLabel(fileName) {
    const ext = getFileExtension(fileName)

    if (ext === 'pdf') return 'Download PDF'
    if (['doc', 'docx'].includes(ext)) return 'Download Word File'
    if (['xls', 'xlsx'].includes(ext)) return 'Download Excel File'
    if (['ppt', 'pptx'].includes(ext)) return 'Download PowerPoint'
    if (ext === 'zip') return 'Download ZIP'

    return 'Download File'
  }

  function cleanFileName(fileName) {
    const base = String(fileName || '').split('?')[0].split('#')[0].split('/').pop()
    const nameWithoutExt = base.includes('.') ? base.slice(0, base.lastIndexOf('.')) : base

    return nameWithoutExt
      .replace(/[_-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, char => char.toUpperCase())
  }

  // Material types that already describe the file, so the label is just "Download".
  const SELF_DESCRIBING_WORDS = ['transcript', 'slides']

  function isSelfDescribingName(cleanName) {
    if (!cleanName || typeof cleanName !== 'string') return false
    const words = cleanName.trim().toLowerCase().split(' ')
    return words.some(word => SELF_DESCRIBING_WORDS.includes(word))
  }

  // Standard listing order: Slides first, then Transcripts, then any other file.
  function getMaterialRank(download) {
    const words = cleanFileName(download?.fileName).toLowerCase().split(' ')
    if (words.includes('slides')) return 0
    if (words.includes('transcript')) return 1
    return 2
  }

  const sortedDownloads = computed(() =>
    [...props.downloads].sort((a, b) => getMaterialRank(a) - getMaterialRank(b))
  )

  function getDisplayLabel(download) {
    const cleanName = cleanFileName(download?.fileName)
    const label = download?.label ||
      (isSelfDescribingName(cleanName) ? 'Download' : getFileLabel(download?.fileName))
    if (!cleanName) return label
    return `${label} ${cleanName}`
  }

  function buildStoragePath(fileName) {
    return `bcst/${props.courseId}/lessons/${props.lessonId}/downloads/${fileName}`
  }

  async function downloadFile(download) {
    if (!download?.fileName) return

    toggleLoadingWithMessage(true, {
      title: 'Downloading file...',
      body: 'Just a few seconds while we prepare the file.'
    })

    try {
      const storage = getStorage(getApp())
      const path = buildStoragePath(download.fileName)
      const fileRef = storageRef(storage, path)
      const url = await getDownloadURL(fileRef)

      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', download.fileName)
      link.setAttribute('target', '_blank')
      // Prevent the opened tab from accessing window.opener (reverse tabnabbing).
      link.setAttribute('rel', 'noopener')
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (error) {
      handleErrors('BcstSupportingMaterials.downloadFile', error)
      reportDownloadError()
    } finally {
      toggleLoadingWithMessage(false, null)
    }
  }
</script>

<template>
  <div v-if="hasDownloads" class="bsm-container">
    <h2 class="bsm-title">Supporting Materials:</h2>
    <AlertNotification
      v-if="showDownloadError"
      type-alert="error"
      :show-alert="showDownloadError"
      class="bsm-download-alert"
      @update:show-alert="dismissDownloadError">
      {{ DOWNLOAD_ERROR_MESSAGE }}
    </AlertNotification>
    <div class="bsm-files">
      <button
        v-for="(download, index) in sortedDownloads"
        :key="`download-${index}`"
        type="button"
        class="bsm-file-link"
        @click="downloadFile(download)">
        <i class="bi bsm-file-icon" :class="getFileIcon(download.fileName)"></i>
        <span class="bsm-file-label">{{ getDisplayLabel(download) }}</span>
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .bsm-container {
    margin-top: 35px;
    margin-bottom: 0;
  }

  .bsm-title {
    color: var(--Gray-700, #495057);
    font-family: 'DM Sans', sans-serif;
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 130%; /* 23.4px */
    letter-spacing: -0.36px;
    margin: 0 0 16px 0;
  }

  .bsm-download-alert {
    margin-bottom: 16px;
  }

  .bsm-files {
    display: flex;
    flex-direction: column;
    gap: 11px;
  }

  .bsm-file-link {
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: var(--Eggplant---Primary, #5C0CB8);
    font-family: 'DM Sans', sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 130%; /* 20.8px */
    letter-spacing: -0.32px;
    text-decoration: none;
    transition: all 0.2s ease;

    &:hover {
      color: var(--Eggplant---Primary, #5C0CB8);
      text-decoration: underline;
    }

    &:focus,
    &:focus-visible,
    &:active,
    &:visited {
      color: var(--Eggplant---Primary, #5C0CB8);
      text-decoration: none;
    }
  }

  .bsm-file-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 21px;
    height: 18px;
    aspect-ratio: 7 / 6;
    margin-right: 8.62px;
    font-size: 18px;
    flex-shrink: 0;
  }

  .bsm-file-label {
    display: inline-flex;
    align-items: center;
    color: var(--Eggplant---Primary, #5C0CB8);
    font-family: 'DM Sans', sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 130%; /* 20.8px */
    letter-spacing: -0.32px;
  }
</style>
